<script setup lang="ts">
import { MediaImage, MediaVideo, Trash, Copy, Link, Upload } from "@iconoir/vue";
import type { Asset } from "@artech-global/types";
import { useFormat } from "~/composables/use-format";
import AssetUpload from "~/components/AssetUpload.vue";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";

defineOptions({ name: "GlobalAsset" });

const { $api } = useNuxtApp();
const { t } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const { set: setTitle } = usePageTitle();

function formatSize(bytes?: number): string {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
}

function dimensions(asset: Asset): string {
  return asset.width && asset.height ? `${asset.width}×${asset.height}` : "—";
}

function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

const MAX_UPLOAD_SIZE = 50 * 1024 * 1024;
const ACCEPT_TYPES = "image/*,video/*";

const defaultFilter = {
  search: "",
  page: 1,
  pageSize: 24,
};

function getAssetType(mimeType: string): "image" | "video" | "file" {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "file";
}

const {
  firstLoading, listLoading, load, itemCount, pageCount, items, filter,
  isEmpty, isSearchEmpty, search, searchDebounced,
} = useList<Asset, typeof defaultFilter>({
  endpoint: "/assets",
  defaultFilter,
});

async function uploadOne(file: File): Promise<Asset> {
  const dim = await readDimensions(file);
  const asset = await $api<Asset>("/assets", {
    method: "POST",
    body: {
      name: file.name,
      originalName: file.name,
      type: getAssetType(file.type),
      mimeType: file.type,
      size: file.size,
      accessLevel: "public",
      ...(dim ? { width: dim.width, height: dim.height } : {}),
    },
  });

  if (asset.status === "ready") return asset;

  const presigned = await $api<{ uploadURL: string; uploadHeaders: Record<string, string> }>(
    `/assets/${asset.id}/presigned-url`,
  );

  await fetch(presigned.uploadURL, {
    method: "PUT",
    body: file,
    headers: { ...presigned.uploadHeaders, "Content-Type": file.type },
  });

  return $api<Asset>(`/assets/${asset.id}/confirm`, { method: "PUT" });
}

async function handleUploaded() {
  toast.success({ title: t("globalAsset.uploadSuccess") });
  await load();
}

function handleUploadError() {
  toast.error({ title: t("globalAsset.uploadFailed") });
}

async function copyId(asset: Asset) {
  await navigator.clipboard.writeText(asset.id);
  toast.success({ title: t("globalAsset.idCopied") });
}

async function copyUrl(asset: Asset) {
  await navigator.clipboard.writeText(asset.url);
  toast.success({ title: t("globalAsset.urlCopied") });
}

async function remove(asset: Asset) {
  const ok = await alert.negative({
    title: t("globalAsset.deleteConfirmTitle"),
    description: t("globalAsset.deleteConfirmBody", { name: asset.name }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;
  try {
    await $api(`/assets/${asset.id}`, { method: "DELETE" });
    toast.success({ title: t("message.deleteSuccess") });
    await load();
  } catch {
    toast.error({ title: t("message.deleteFailed") });
  }
}

onMounted(async () => {
  setTitle(t("globalAsset.title"));
  await load();
});

watch(() => filter.search, () => searchDebounced());
watch(() => filter.page, () => load());
</script>

<template>
  <ListPage :title="t('globalAsset.title')" :first-loading="firstLoading" :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty">
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('globalAsset.itemsSuffix')}` }}
    </template>

    <template #actions>
      <AssetUpload type="button" file-list="none" multiple :accept="ACCEPT_TYPES"
        :max-file-size="MAX_UPLOAD_SIZE"
        :on-upload="uploadOne" @uploaded="handleUploaded" @error="handleUploadError">
        <template #trigger="{ api, disabled }">
          <Button variant="primary" size="small" :disabled="disabled" @click="api.openFilePicker()">
            <Upload class="icon" />
            {{ t("globalAsset.upload") }}
          </Button>
        </template>
      </AssetUpload>
    </template>

    <template #empty>
      <ListEmpty :title="t('globalAsset.emptyTitle')" :subtitle="t('globalAsset.emptySubtitle')">
        <template #action>
          <AssetUpload type="button" file-list="none" multiple accept="image/*"
            :on-upload="uploadOne" @uploaded="handleUploaded" @error="handleUploadError">
            <template #trigger="{ api, disabled }">
              <Button variant="primary" size="small" :disabled="disabled" @click="api.openFilePicker()">
                <Upload class="icon" />
                {{ t("globalAsset.upload") }}
              </Button>
            </template>
          </AssetUpload>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty :title="t('globalAsset.emptySearchTitle')" :subtitle="t('globalAsset.emptySearchSubtitle')" />
    </template>

    <template #filter-right>
      <FormSearch v-model="filter.search" :placeholder="t('field.search')" size="sm" class="w-full md:w-1/2" />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'preview', label: '', class: 'min-w-[56px]' },
          { key: 'name', label: t('globalAsset.colName'), class: 'md:min-w-[240px]' },
          { key: 'type', label: t('globalAsset.colType'), class: 'min-w-[96px]' },
          { key: 'dimensions', label: t('globalAsset.colDimensions'), class: 'min-w-[112px]' },
          { key: 'size', label: t('globalAsset.colSize'), class: 'min-w-[96px]' },
          { key: 'createTime', label: t('field.createTime'), class: 'min-w-[160px]' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[128px]' },
        ]"
        :items="items"
      >
        <template #cell-preview="{ row }">
          <div class="global-asset__thumb flex items-center justify-center radius-small overflow-hidden bg-surface">
            <img v-if="row.url && row.type === 'image'" :src="row.url" :alt="row.name" loading="lazy" class="w-full h-full" style="object-fit: cover" />
            <MediaVideo v-else-if="row.type === 'video'" class="icon content-neutral-light" />
            <MediaImage v-else class="icon content-neutral-light" />
          </div>
        </template>

        <template #cell-name="{ row }">
          <span class="font-semibold">{{ row.name || row.originalName || "—" }}</span>
        </template>

        <template #cell-type="{ row }">
          <Badge intent="neutral" emphasis="medium" size="small">{{ row.type }}</Badge>
        </template>

        <template #cell-dimensions="{ row }">{{ dimensions(row) }}</template>

        <template #cell-size="{ row }">{{ formatSize(row.size) }}</template>

        <template #cell-createTime="{ row }">{{ d(row.createTime, 'datetime') }}</template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon variant="neutral-flat" size="small" :title="t('globalAsset.copyId')" @click.stop="copyId(row)">
              <Copy class="icon" />
            </ButtonIcon>
            <ButtonIcon variant="neutral-flat" size="small" :title="t('globalAsset.copyUrl')" @click.stop="copyUrl(row)">
              <Link class="icon" />
            </ButtonIcon>
            <ButtonIcon variant="neutral-flat" size="small" :title="t('action.delete')" @click.stop="remove(row)">
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination v-if="pageCount > 1" :count="itemCount" :page-size="filter.pageSize" v-model:page="filter.page" />
    </template>
  </ListPage>
</template>

<style scoped lang="scss">
.global-asset__thumb {
  width: token("dm-40");
  height: token("dm-40");
}
</style>
